import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.autoflowapp.net/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth Service
export const authService = {
  login: async (email, password) => {
  const response = await axios.post(`${API_URL}/auth/login`, { email, password });
  
  const data = response.data;

  // 🔥 GUARDAR TOKEN
  localStorage.setItem('access_token', data.access_token);

  return data;
},

  logout: async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  getCurrentUser: async () => {
    const response = await axios.get(`${API_URL}/auth/me`, { headers: getAuthHeaders() });
    return response.data;
  }
};

// Agency Service (V2 - /api/agencies)
export const agencyService = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/agencies`, { 
      headers: getAuthHeaders(),
      params 
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API_URL}/agencies/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Get current user's agency (for AGENCY_ADMIN)
  getMyAgency: async () => {
    const response = await axios.get(`${API_URL}/agencies/my`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Update current user's agency (for AGENCY_ADMIN)
  updateMyAgency: async (data) => {
    const response = await axios.patch(`${API_URL}/agencies/my`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  create: async (data, adminEmail, adminPassword, adminName) => {
    const params = new URLSearchParams({
      admin_email: adminEmail,
      admin_password: adminPassword,
      admin_name: adminName
    });
    const response = await axios.post(`${API_URL}/agencies?${params}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/agencies/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (id, hardDelete = false) => {
    const response = await axios.delete(`${API_URL}/agencies/${id}?hard_delete=${hardDelete}`, { headers: getAuthHeaders() });
    return response.data;
  },

  getStats: async (id) => {
    const response = await axios.get(`${API_URL}/agencies/${id}/stats`, { headers: getAuthHeaders() });
    return response.data;
  },

  registerPayment: async (agencyId, paymentData) => {
    const response = await axios.post(`${API_URL}/agencies/${agencyId}/register-payment`, paymentData, { headers: getAuthHeaders() });
    return response.data;
  },

  getPayments: async (agencyId) => {
    const response = await axios.get(`${API_URL}/agencies/${agencyId}/payments`, { headers: getAuthHeaders() });
    return response.data;
  }
};

// User Service (V2 - /api/users)
export const userService = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/users`, { 
      headers: getAuthHeaders(),
      params 
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API_URL}/users/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  create: async (data) => {
    const response = await axios.post(`${API_URL}/users`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/users/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (id, hardDelete = false) => {
    const response = await axios.delete(`${API_URL}/users/${id}?hard_delete=${hardDelete}`, { headers: getAuthHeaders() });
    return response.data;
  },

  resetPassword: async (id, newPassword) => {
    const response = await axios.post(`${API_URL}/users/${id}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, { headers: getAuthHeaders() });
    return response.data;
  }
};

// Vehicle Service (V2 - /api/vehicles) - replaces carService
export const vehicleService = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/vehicles`, { 
      headers: getAuthHeaders(),
      params 
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API_URL}/vehicles/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  create: async (data, agencyId = null) => {
    const params = agencyId ? `?agencyId=${agencyId}` : '';
    const response = await axios.post(`${API_URL}/vehicles${params}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/vehicles/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await axios.patch(`${API_URL}/vehicles/${id}/status?status=${status}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (id, hardDelete = false) => {
    const response = await axios.delete(`${API_URL}/vehicles/${id}?hard_delete=${hardDelete}`, { headers: getAuthHeaders() });
    return response.data;
  },

  getStats: async (id) => {
    const response = await axios.get(`${API_URL}/vehicles/${id}/stats`, { headers: getAuthHeaders() });
    return response.data;
  },

  trackView: async (id) => {
    await axios.post(`${API_URL}/vehicles/${id}/track-view`);
  }
};

// Legacy alias for backward compatibility
export const carService = vehicleService;

// Channel Service (V2 - /api/channels)
export const channelService = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/channels`, { 
      headers: getAuthHeaders(),
      params 
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API_URL}/channels/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  create: async (data, agencyId = null) => {
    const params = agencyId ? `?agencyId=${agencyId}` : '';
    const response = await axios.post(`${API_URL}/channels${params}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/channels/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  updateToNumber: async (id, toNumber) => {
    const response = await axios.patch(`${API_URL}/channels/${id}/to-number?toNumber=${encodeURIComponent(toNumber)}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (id, hardDelete = false) => {
    const response = await axios.delete(`${API_URL}/channels/${id}?hard_delete=${hardDelete}`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Public endpoint for bots
  resolveAgency: async (type, toNumber) => {
    const response = await axios.post(`${API_URL}/channels/resolve-agency`, { type, toNumber });
    return response.data;
  }
};

// Lead Service (V2 - /api/leads)
export const leadService = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API_URL}/leads`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API_URL}/leads/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  create: async (data, agencyId = null) => {
    const params = agencyId ? `?agencyId=${agencyId}` : '';
    const response = await axios.post(`${API_URL}/leads${params}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  createFromChannel: async (data, agencyId, channelId = null) => {
    let params = `?agencyId=${agencyId}`;
    if (channelId) params += `&channelId=${channelId}`;
    const response = await axios.post(`${API_URL}/leads/from-channel${params}`, data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/leads/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  updateStatus: async (id, newStatus) => {
    const response = await axios.patch(
      `${API_URL}/leads/${id}/status?status=${newStatus}`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  addNote: async (id, text) => {
    const response = await axios.post(
      `${API_URL}/leads/${id}/notes`,
      { text },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  updateTags: async (id, tags) => {
    const response = await axios.post(
      `${API_URL}/leads/${id}/tags`,
      { tags },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/leads/${id}`, { headers: getAuthHeaders() });
    return response.data;
  },

  toggleAgent: async (id, agentActive) => {
    const response = await axios.patch(
      `${API_URL}/leads/${id}/agent-toggle?agent_active=${agentActive}`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
};

// Public Service (V2 - /api/public)
export const publicService = {
  getAgencyBySlug: async (slug) => {
    const response = await axios.get(`${API_URL}/public/agencies/${slug}`);
    return response.data;
  },

  getAgencyVehicles: async (slug, params = {}) => {
    const response = await axios.get(`${API_URL}/public/agencies/${slug}/vehicles`, { params });
    return response.data;
  },

  getVehicleDetail: async (slug, vehicleId) => {
    const response = await axios.get(`${API_URL}/public/agencies/${slug}/vehicles/${vehicleId}`);
    return response.data;
  },

  getVehiclesByAgencyId: async (agencyId, params = {}) => {
    const response = await axios.get(`${API_URL}/public/vehicles/by-agency/${agencyId}`, { params });
    return response.data;
  },

  getAllAgencies: async () => {
    const response = await axios.get(`${API_URL}/public/agencies`);
    return response.data;
  },

  getAllVehicles: async (params = {}) => {
    const response = await axios.get(`${API_URL}/public/vehicles`, { params });
    return response.data;
  },

  trackVehicleView: async (vehicleId) => {
    try {
      await axios.post(`${API_URL}/vehicles/${vehicleId}/track-view`);
    } catch {
      // Silently fail — view tracking must not interrupt UX
    }
  }
};

// Note Service (keeping for backward compatibility but may need backend implementation)
export const noteService = {
  getAll: async () => {
    try {
      const response = await axios.get(`${API_URL}/notes`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      // Notes endpoint may not exist in V2 yet
      console.warn('Notes endpoint not available');
      return [];
    }
  },

  create: async (data) => {
    const response = await axios.post(`${API_URL}/notes`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  update: async (id, data) => {
    const response = await axios.patch(`${API_URL}/notes/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API_URL}/notes/${id}`, { headers: getAuthHeaders() });
    return response.data;
  }
};

// Dashboard Stats
export const dashboardService = {
  getGlobalStats: async () => {
    // Aggregate stats from multiple endpoints
    try {
      const [agencies, users, vehicles, leads] = await Promise.all([
        agencyService.getAll().catch(() => []),
        userService.getAll().catch(() => []),
        vehicleService.getAll().catch(() => []),
        leadService.getAll().catch(() => [])
      ]);
      
      return {
        totalAgencies: agencies.length,
        totalUsers: users.length,
        totalVehicles: vehicles.length,
        publishedVehicles: vehicles.filter(v => v.status === 'published').length,
        totalLeads: leads.length,
        newLeads: leads.filter(l => l.status === 'new').length
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        totalAgencies: 0,
        totalUsers: 0,
        totalVehicles: 0,
        publishedVehicles: 0,
        totalLeads: 0,
        newLeads: 0
      };
    }
  }
};

// Upload Service (R2 via Backend Proxy)
export const uploadService = {
  getConfig: async () => {
    const response = await axios.get(`${API_URL}/uploads/config`, { headers: getAuthHeaders() });
    return response.data;
  },

  getPresignedUrl: async (data) => {
    const response = await axios.post(`${API_URL}/uploads/presigned-url`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  getVehicleBatchUrls: async (data) => {
    const response = await axios.post(`${API_URL}/uploads/vehicle-batch`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  // Upload file through backend proxy (avoids CORS issues)
  uploadFile: async (file, uploadType, agencyId, vehicleId = null, imageIndex = null, onProgress = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_type', uploadType);
    formData.append('agency_id', agencyId);
    if (vehicleId) formData.append('vehicle_id', vehicleId);
    if (imageIndex) formData.append('image_index', imageIndex.toString());

    const config = {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    };

    if (onProgress) {
      config.onUploadProgress = onProgress;
    }

    const response = await axios.post(`${API_URL}/uploads/upload`, formData, config);
    return response.data;
  },

  // Legacy: Upload directly to R2 using presigned URL (may fail due to CORS)
  uploadToR2: async (presignedUrl, file, onProgress = null) => {
    const config = {
      headers: {
        'Content-Type': 'image/webp'
      }
    };

    if (onProgress) {
      config.onUploadProgress = onProgress;
    }

    const response = await axios.put(presignedUrl, file, config);
    return response.data;
  }
};

// Notification Service
export const notificationService = {
  getAll: async (limit = 50, unreadOnly = false) => {
    const params = new URLSearchParams({ limit, unread_only: unreadOnly });
    const response = await axios.get(`${API_URL}/notifications?${params}`, { headers: getAuthHeaders() });
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await axios.get(`${API_URL}/notifications/count`, { headers: getAuthHeaders() });
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await axios.post(`${API_URL}/notifications/${notificationId}/read`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await axios.post(`${API_URL}/notifications/read-all`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  delete: async (notificationId) => {
    const response = await axios.delete(`${API_URL}/notifications/${notificationId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  checkPayments: async () => {
    const response = await axios.post(`${API_URL}/notifications/check-payments`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  testEmail: async () => {
    const response = await axios.post(`${API_URL}/notifications/test-email`, {}, { headers: getAuthHeaders() });
    return response.data;
  }
};

